/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconArrowDown } from "./icons/arrow-down.js";

const styles = css`
  :host { display: block; height: 100%; min-height: 0; width: 100%; }
  .root { display: flex; height: 100%; min-height: 0; overflow: hidden; position: relative; width: 100%; }
  .viewport { box-sizing: border-box; contain: content; height: 100%; min-height: 0; min-width: 0; overscroll-behavior: contain; overflow-x: hidden; overflow-y: auto; scrollbar-gutter: stable; width: 100%; }
  .content { display: flex; flex-direction: column; gap: 32px; min-height: 100%; }
  .to-end {
    align-items: center;
    background: var(--background, #fff);
    border: 1px solid var(--border, #e4e4e7);
    border-radius: 16px;
    bottom: var(--composer-clearance, 160px);
    box-sizing: border-box;
    color: var(--foreground, #18181b);
    cursor: pointer;
    display: flex;
    height: 28px;
    justify-content: center;
    left: 50%;
    opacity: 1;
    padding: 0;
    position: absolute;
    transform: translate(-50%, 0) scale(1);
    transition: background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease, opacity 200ms ease, transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
    width: 28px;
  }
  .to-end:hover { background: var(--muted, #f4f4f5); }
  .to-end:focus-visible { border-color: var(--ring, #18181b); box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%)); outline: none; }
  .to-end.hidden { opacity: 0; pointer-events: none; transform: translate(-50%, 100%) scale(0.95); transition-duration: 400ms; transition-timing-function: cubic-bezier(0.7, 0, 0.84, 0); }
  .to-end-icon { height: 16px; width: 16px; }
  .sr-only { height: 1px; margin: -1px; overflow: hidden; padding: 0; position: absolute; width: 1px; clip: rect(0, 0, 0, 0); white-space: nowrap; }
  @media (prefers-reduced-motion: reduce) { .to-end { transition: none; } }
`;

export class AppMessageScroller extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppMessageScroller)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #atEnd;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #attr0;
  #attrLast0;
  #attrLast1;
  #viewport = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="viewport"]') : null; } }; })();
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setAtEnd(value) {
    if (Object.is(this.#atEnd, value)) return;
    this.#atEnd = value;
    this.#updateBindings('atEnd');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const el = this.#viewport.current;
    if (!el)
        return;
    const update = () => { this.#setAtEnd(el.scrollHeight - el.scrollTop - el.clientHeight < 8); this.dispatchEvent(new CustomEvent("scroll-state-change", { bubbles: true, composed: true, detail: { atEnd: this.#atEnd } })); };
    const observer = new ResizeObserver(() => { if (this.#atEnd)
        el.scrollTo({ top: el.scrollHeight }); update(); });
    observer.observe(el);
    const content = el.querySelector(".content");
    if (content)
        observer.observe(content);
    el.addEventListener("scroll", update, { passive: true });
    (this as HTMLElement & {
        scrollToEnd: (behavior?: ScrollBehavior) => void;
    }).scrollToEnd = (behavior = "smooth") => el.scrollTo({ top: el.scrollHeight, behavior: behavior });
    update();
    return () => { observer.disconnect(); el.removeEventListener("scroll", update); };
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#atEnd = true;
  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="root"><div data-argon-ref="viewport" class="viewport"><div class="content"><slot></slot></div></div><button type="button" class="${this.#escAttr(`to-end ${this.#atEnd ? "hidden" : ""}`)}" tabIndex="${this.#atEnd ? -1 : 0}" data-argon="3245" data-argon-bind="0"><icon-arrow-down class="to-end-icon"><!--argon-text:0-->${""}<!--/argon-text:0--></icon-arrow-down><span class="sr-only">Scroll to end</span></button></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'atEnd') && this.#attr0) {
      const __argonNext = `${`to-end ${this.#atEnd ? "hidden" : ""}`}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'atEnd') && this.#attr0) {
      const __argonNext = `${this.#atEnd ? -1 : 0}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('tabIndex', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3245').forEach(el => this.#listen(el, '3245', 'click', () => this.#viewport.current?.scrollTo({ top: this.#viewport.current.scrollHeight, behavior: "smooth" })));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
